/**
 * <media-slider>: the inner product-card media carousel.
 *
 * Arrow navigation with conveyor-belt infinite looping on a transform-based
 * track. By default it registers no pointer/touch listeners on the track, so
 * swipes pass straight through to the parent <carousel-slider>'s native
 * scroller and the arrows (which sit above the track) are the only navigation.
 *
 * Swipeable mode (data-swipeable): pointer/touch drag drives navigation as
 * well. Used on collection grids and the quick add drawer where there's no
 * parent carousel and swipe is the expected interaction.
 *
 * The gesture model mimics a paging UIScrollView so a touch never has to wait:
 * a new pointerdown catches any in-flight settle animation mid-flight (freezing
 * the track under the finger, recentring, and continuing as a fresh drag), the
 * drag loop is read-free and rAF-coalesced (measured once per gesture, written
 * in px), and release commits on distance OR flick velocity (one slide per
 * gesture). Gesture-driven settles use short, momentum-shaped durations; arrow
 * clicks keep their deliberate 650ms transition. Reduced motion stays instant
 * on every path.
 *
 * Expected markup (see snippets/product-card.liquid):
 *
 *   <media-slider>
 *     <div data-js="media-slider-track">
 *       <a href="…"><img …></a>
 *       <a href="…"><img …></a>
 *     </div>
 *   </media-slider>
 *
 * Public API: el.next(), el.prev().
 *
 * Reflected state: data-at-origin="true|false" is set on the host after every
 * step (and on init) from the net next-minus-prev count. The product-card hover
 * overlay reads it via CSS and only fades in when the slider is back at its
 * original first slide.
 */
(function () {
  if (window.customElements.get("media-slider")) return;

  const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)");

  /** Minimum drag distance (px) to commit a slide change. */
  const SWIPE_THRESHOLD = 30;

  /**
   * Default / arrow-click transition duration (ms). A deliberate aesthetic on
   * product cards — only gesture-driven settles override it (see D4).
   */
  const DEFAULT_DURATION_MS = 650;

  /**
   * A flick commits a step above this speed (px/ms) even under the distance
   * threshold, as long as the flick direction matches the drag direction.
   * Tuned by judgement — expect a tweaking round after real-device testing.
   */
  const FLICK_VELOCITY = 0.4;

  /** Lower/upper clamp bounds (ms) for gesture-driven commit settle durations. */
  const SETTLE_MIN_MS = 160;
  const SETTLE_MAX_MS = 380;

  /** Return duration (ms) for snap-backs and pointercancel with no velocity. */
  const SNAP_BACK_MS = 250;

  /**
   * Floor (px/ms) for the velocity used in the settle-duration divisor, so a
   * near-zero velocity can't blow the duration past the clamp ceiling.
   */
  const MIN_SETTLE_VELOCITY = 0.8;

  /** Velocity ring-buffer window (ms) and sample cap. */
  const VELOCITY_WINDOW_MS = 100;
  const VELOCITY_MAX_SAMPLES = 6;

  /**
   * Clamp a number to an inclusive range.
   * @param {number} n
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

  class MediaSlider extends HTMLElement {
    constructor() {
      super();
      this._initialised = false;
      this._destroyed = false;
      /** Logical index in the current DOM order (0 = first child). */
      this._index = 0;
      /**
       * Net navigation offset since init: increments on every next, decrements
       * on every prev. Unaffected by _recenter (which always pins _index to the
       * middle child). Used to tell when we're back at the original first slide.
       */
      this._navOffset = 0;
      /** Lock to coordinate transitionend with arrow clicks. */
      this._animating = false;
      this._listeners = [];

      this._swipeable = false;
      this._dragging = false;
      this._startX = 0;
      this._currentX = 0;
      this._dragDelta = 0;
      this._settleTimer = 0;
      /** Pending drag-write animation frame id (0 = none scheduled). */
      this._rafId = 0;
      /** Metrics measured once per gesture on pointerdown. */
      this._gestureMetrics = null;
      /** Velocity ring buffer of recent {x, t} pointer samples. */
      this._samples = [];
      /** True while the current gesture began by catching an in-flight settle. */
      this._gestureCaught = false;
    }

    connectedCallback() {
      if (this._initialised || this._destroyed) return;

      this._track = this.querySelector(
        ':scope > [data-js="media-slider-track"]',
      );
      if (!this._track || this._track.children.length === 0) return;

      // Single-slide cards render no arrows and no listeners, which saves a
      // re-layout and avoids ARIA noise.
      if (this._track.children.length === 1) {
        this._initialised = true;
        return;
      }

      this._swipeable = this.hasAttribute("data-swipeable");
      this._init();
    }

    disconnectedCallback() {
      // The parent <carousel-slider> conveyor belt moves slides via
      // appendChild, which fires disconnect then connect synchronously on every
      // nested custom element. Defer real teardown to a microtask so a same-tick reconnect bails out and the slider stays alive (arrows and listeners intact).
      queueMicrotask(() => {
        if (this.isConnected) return;
        this._destroyed = true;
        this._listeners.forEach(({ target, type, fn, opts }) => {
          target.removeEventListener(type, fn, opts);
        });
        this._listeners.length = 0;
        clearTimeout(this._settleTimer);
        if (this._rafId) {
          cancelAnimationFrame(this._rafId);
          this._rafId = 0;
        }
      });
    }

    next(opts = {}) {
      this._step(1, opts.instant);
    }

    prev(opts = {}) {
      this._step(-1, opts.instant);
    }

    /**
     * Navigate directly to the slide with the given original index.
     * @param {number|string} targetOriginalIndex
     * @param {object} [opts]
     * @param {boolean} [opts.instant=false]
     */
    goToOriginalIndex(targetOriginalIndex, opts = {}) {
      if (!this._initialised) return;
      const targetStr = String(targetOriginalIndex);
      const targetSlide = Array.from(this._track.children).find(
        (slide) => slide.dataset.freshOriginalIndex === targetStr,
      );
      if (!targetSlide) return;

      const targetIndex = Array.from(this._track.children).indexOf(targetSlide);
      const dir = targetIndex - this._index;
      if (dir === 0) return;

      if (targetStr === "0") {
        this._navOffset = -dir;
      } else if (targetStr === String(this._slides.length - 1)) {
        this._navOffset = this._navOffset === -dir ? 1 - dir : this._navOffset;
      }

      this._step(dir, opts.instant);
    }

    /**
     * Swap data-src -> src on the currently visible slide. Called by the parent
     * <carousel-slider> when its IntersectionObserver decides this card is in
     * view, since the parent can't know which inner slide is active after a
     * recenter.
     */
    loadActive() {
      // If _init hasn't run yet (still parsing), defer until we know _index.
      if (!this._initialised) {
        this._loadActivePending = true;
        return;
      }
      this._loadSlide(this._index);
      this._warmNeighbours();
    }

    /**
     * Swap data-src -> src on every slide. For hosts whose layout keeps
     * neighbouring slides visible (e.g. the quick-add drawer's peek track),
     * where one-ahead lazy loading would leave on-screen slides unloaded.
     * Deferred to idle so it never competes with the active image.
     */
    loadAll() {
      if (!this._initialised) {
        this._loadAllPending = true;
        return;
      }
      const warm = () => {
        for (let i = 0; i < this._track.children.length; i += 1) {
          this._loadSlide(i);
        }
      };
      if (typeof requestIdleCallback === "function") {
        requestIdleCallback(warm, { timeout: 500 });
      } else {
        setTimeout(warm, 250);
      }
    }

    /**
     * Warm both slides adjacent to the active one — the 2nd slide (index+1)
     * and, after the conveyor recentre, the last slide (index-1) — so the
     * first step in either direction is already decoded before the user swipes
     * or clicks an arrow. Steps beyond the neighbours are pre-warmed one ahead
     * in _step(), so only these two ever showed a load-in.
     *
     * Deferred to idle so it never competes with the active image, but with a
     * timeout so a busy main thread (e.g. mid-hydration) can't starve it and
     * leave the first swipe loading on demand.
     */
    _warmNeighbours() {
      const warm = () => {
        this._loadSlide(this._index + 1);
        this._loadSlide(this._index - 1);
      };
      if (typeof requestIdleCallback === "function") {
        requestIdleCallback(warm, { timeout: 500 });
      } else {
        setTimeout(warm, 250);
      }
    }

    _init() {
      this._initialised = true;

      this._i18n = {
        prev: this.dataset.i18nPrev || "Previous slide",
        next: this.dataset.i18nNext || "Next slide",
      };

      // Decode-warm whichever images the parent <carousel-slider> has already
      // swapped in (via its IntersectionObserver, typically just this card's
      // first image). Secondary images load on demand in _step() when the user
      // clicks an arrow.
      this._track.querySelectorAll("img").forEach((img) => {
        const decode = () => {
          if (typeof img.decode === "function") img.decode().catch(() => {});
        };
        if (img.complete && img.naturalWidth > 0) {
          decode();
        } else {
          img.addEventListener("load", decode, { once: true });
        }
      });

      this._slides = Array.from(this._track.children);
      this._slides.forEach((slide, i) => {
        slide.dataset.freshOriginalIndex = String(i);
      });

      // Initial reflected state: we're at the original first slide.
      this.dataset.atOrigin = "true";

      // Arrows render in every mode. In swipeable mode the drag handler also
      // binds, but only for touch/pen pointers: mouse clicks pass through to the
      // slide anchor, and desktop users navigate via the arrows on hover.
      this._setupArrows();
      if (this._swipeable) {
        this._setupSwipe();
      }

      if (this.hasAttribute("data-progress-bar") && this._track.children.length > 1) {
        const progressContainer = document.createElement("div");
        progressContainer.className = "media-slider__progress";
        progressContainer.setAttribute("aria-hidden", "true");
        const progressBar = document.createElement("div");
        progressBar.className = "media-slider__progress-bar";
        progressContainer.appendChild(progressBar);
        this.appendChild(progressContainer);
      }

      // Recenter the DOM so the active slide sits in the middle of
      // the track. This gives us buffer slides on both sides, so a
      // single arrow click never animates into empty space.
      this._recenter();
      this._syncActiveSlide();

      this._on(this._track, "transitionend", this._onTransitionEnd.bind(this));

      // If `loadActive()`/`loadAll()` were called before init finished,
      // honour them now that we know which slide is active.
      if (this._loadActivePending) {
        this._loadActivePending = false;
        this._loadSlide(this._index);
        this._warmNeighbours();
      }
      if (this._loadAllPending) {
        this._loadAllPending = false;
        this.loadAll();
      }
    }

    /**
     * Rotate the DOM so the active slide sits at the middle child. The visual
     * position is unchanged: the transform snaps to compensate. Called on init
     * and after every animation settle, so the next nav always has buffer
     * slides on both sides and never animates into empty space.
     */
    _recenter() {
      const total = this._track.children.length;
      if (total < 2) return;
      const desired = Math.floor(total / 2);
      let shift = this._index - desired;
      const rotated = shift !== 0;
      while (shift > 0) {
        this._track.appendChild(this._track.firstElementChild);
        this._index -= 1;
        shift -= 1;
      }
      while (shift < 0) {
        this._track.insertBefore(
          this._track.lastElementChild,
          this._track.firstElementChild,
        );
        this._index += 1;
        shift += 1;
      }
      if (rotated) {
        // Safari (observed on 26.x) re-resolves the track's percentage
        // width against the slider's full width after slides are rotated,
        // inflating aspect-ratio slides until an unrelated relayout (the
        // drawer's tall-slide bug). Forcing a fresh layout pass with a
        // synchronous display toggle corrects it; nothing paints between
        // the two writes, so the toggle is visually free.
        const prevDisplay = this._track.style.display;
        this._track.style.display = "none";
        void this._track.offsetHeight;
        this._track.style.display = prevDisplay;
      }
      this._slides = Array.from(this._track.children);
      this._setTransform(this._index, false);
    }

    _on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this._listeners.push({ target, type, fn, opts });
    }

    _setupArrows() {
      this._prevButton = this._buildArrow("prev");
      this._nextButton = this._buildArrow("next");
      this.appendChild(this._prevButton);
      this.appendChild(this._nextButton);
      this._on(this._prevButton, "click", (event) => {
        // Stop the click bubbling to the wrapping <a> in the card.
        event.preventDefault();
        event.stopPropagation();
        this.prev();
      });
      this._on(this._nextButton, "click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.next();
      });
    }

    _buildArrow(dir) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `media-slider__arrow media-slider__arrow--${dir}`;
      btn.dataset.js = `media-slider-arrow-${dir}`;
      btn.setAttribute(
        "aria-label",
        dir === "prev" ? this._i18n.prev : this._i18n.next,
      );

      // Look for an icon template on a containing host first (carousel-slider,
      // athos-carousel, or a section root that opts in via
      // data-media-slider-host). Fall back to a document-level template so plain
      // grids can still render arrows.
      const host = this.closest(
        "carousel-slider, athos-carousel, [data-media-slider-host]",
      );
      let tpl = host
        ? host.querySelector(
            `:scope > template[data-js="carousel-icon-${dir}"]`,
          )
        : null;
      if (!tpl) {
        tpl = document.querySelector(
          `template[data-js="carousel-icon-${dir}"]`,
        );
      }
      if (tpl) {
        btn.appendChild(tpl.content.cloneNode(true));
      }
      return btn;
    }

    /* swipe / drag navigation */

    /**
     * Set up pointer-based drag navigation for swipeable mode. Touch/pen drags
     * move between slides; arrows still render for mouse users.
     */
    _setupSwipe() {
      const onPointerDown = (event) => {
        // Only respond to primary pointer (left mouse / first touch).
        if (event.button && event.button !== 0) return;

        // Mouse users get the arrow buttons, so let mouse pointerdown fall
        // through to the wrapping <a> on the slide. Mirrors the
        // pointerType === "touch" guard in assets/carousel.js.
        if (event.pointerType !== "touch" && event.pointerType !== "pen") {
          return;
        }

        // Let native buttons handle their own interactions without triggering a drag
        if (event.target.closest("button")) return;

        // Second-pointer guard: ignore extra fingers while a drag is live.
        if (this._dragging) return;

        // Measure once per gesture — the drag loop is read-free from here on.
        this._gestureMetrics = this._measureGesture();

        // Touch interrupts everything (D2): if a settle animation is running,
        // freeze the track where it currently is and carry that offset into the
        // fresh drag so the finger picks the content up mid-flight.
        const carriedPx = this._animating ? this._catchMidFlight() : 0;

        this._dragging = true;
        // Direction isn't known yet at touch-down, so warm both neighbours —
        // after _recenter() they're always this._index ± 1 in DOM order.
        this._loadSlide(this._index + 1);
        this._loadSlide(this._index - 1);
        this._gestureCaught = carriedPx !== 0;
        this._dragDelta = carriedPx;
        this._startX = event.clientX - carriedPx;
        this._currentX = event.clientX;
        this._samples.length = 0;
        this._pushSample(event);

        this._track.style.transition = "none";
        // Track live during the drag: promote via data-dragging, drop
        // data-animating so the progress bar isn't smoothed by its settle
        // transition (see snippets/product-card.liquid).
        delete this._track.dataset.animating;
        this._track.dataset.dragging = "true";
        this.setPointerCapture(event.pointerId);
      };

      const onPointerMove = (event) => {
        if (!this._dragging) return;
        this._currentX = event.clientX;
        this._dragDelta = this._currentX - this._startX;
        this._pushSample(event);
        this._scheduleDragFrame();
      };

      const onPointerUp = (event) => {
        if (!this._dragging) return;
        this._dragging = false;
        this._flushDragFrame();

        try {
          this.releasePointerCapture(event.pointerId);
        } catch (_e) {
          /* already released */
        }

        const delta = this._dragDelta;
        const absDelta = Math.abs(delta);
        const velocity = this._computeVelocity();
        this._samples.length = 0;
        const stepPx = this._gestureMetrics ? this._gestureMetrics.stepPx : 1;

        // Commit on distance OR a flick whose velocity agrees with the drag.
        const flick =
          delta !== 0 &&
          Math.abs(velocity) > FLICK_VELOCITY &&
          Math.sign(velocity) === Math.sign(delta);

        // A gesture that caught an in-flight settle starts with _dragDelta
        // pre-seeded by the carried offset, so the eager 30px threshold would
        // read a stationary catch-and-release as a deliberate drag and step
        // backwards. Caught gestures use nearest-page (half a step) instead;
        // fresh drags keep the eager threshold.
        const distanceCommit = this._gestureCaught
          ? absDelta > stepPx / 2
          : absDelta > SWIPE_THRESHOLD;

        if (distanceCommit || flick) {
          // One slide per gesture (D3). Direction from the drag sign.
          const dir = delta < 0 ? 1 : -1;
          const remainingPx = Math.abs(-dir * stepPx - delta);
          const durationMs = REDUCED_MOTION.matches
            ? DEFAULT_DURATION_MS
            : clamp(
                remainingPx / Math.max(Math.abs(velocity), MIN_SETTLE_VELOCITY),
                SETTLE_MIN_MS,
                SETTLE_MAX_MS,
              );
          this._step(dir, false, durationMs);
        } else {
          // Snap back to the current slide. _dragDelta resets on settle so the
          // synthetic post-drag click is still suppressed first.
          this._settleBack(SNAP_BACK_MS);
        }
      };

      const onPointerCancel = (event) => {
        if (!this._dragging) return;
        this._dragging = false;
        this._flushDragFrame();

        try {
          this.releasePointerCapture(event.pointerId);
        } catch (_e) {
          /* already released */
        }

        this._samples.length = 0;
        // iOS steals the gesture for vertical scroll — animate back to rest
        // instead of hard-snapping (still instant under reduced motion).
        this._settleBack(SNAP_BACK_MS);
      };

      this._on(this, "pointerdown", onPointerDown);
      this._on(this, "pointermove", onPointerMove);
      this._on(this, "pointerup", onPointerUp);
      this._on(this, "pointercancel", onPointerCancel);

      // Prevent native drag on images so the pointer events work.
      this._on(this, "dragstart", (e) => e.preventDefault());

      // Suppress clicks that fire after a drag gesture to stop the
      // wrapping <a> from navigating. Keys off _dragDelta, which stays non-zero
      // (including after a catch) until the gesture settles.
      this._on(
        this,
        "click",
        (event) => {
          if (Math.abs(this._dragDelta) > 5 || this._dragging) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true,
      );
    }

    /**
     * Measure the track once per gesture so the drag loop can stay read-free.
     * All px maths keys off the slide width (track.offsetWidth), which honours
     * the drawer's 45.45% peek-track layout.
     * @returns {{trackWidth: number, gapPx: number, stepPx: number}}
     */
    _measureGesture() {
      const styles = getComputedStyle(this._track);
      const gapPx = parseFloat(styles.columnGap || styles.gap) || 0;
      const trackWidth = this._track.offsetWidth || 1;
      return { trackWidth, gapPx, stepPx: trackWidth + gapPx };
    }

    /**
     * Push a pointer sample into the velocity ring buffer, capped in length.
     * @param {PointerEvent} event
     */
    _pushSample(event) {
      this._samples.push({ x: event.clientX, t: event.timeStamp });
      if (this._samples.length > VELOCITY_MAX_SAMPLES) this._samples.shift();
    }

    /**
     * Instantaneous drag velocity (px/ms) over the last VELOCITY_WINDOW_MS,
     * signed by direction. Positive = rightward (toward the previous slide).
     * @returns {number}
     */
    _computeVelocity() {
      const samples = this._samples;
      if (samples.length < 2) return 0;
      const last = samples[samples.length - 1];
      let oldest = samples[0];
      for (let i = 0; i < samples.length; i += 1) {
        if (last.t - samples[i].t <= VELOCITY_WINDOW_MS) {
          oldest = samples[i];
          break;
        }
      }
      const dt = last.t - oldest.t;
      if (dt <= 0) return 0;
      return (last.x - oldest.x) / dt;
    }

    /**
     * Schedule a single rAF that writes the latest drag transform + progress,
     * coalescing bursts of pointermove samples into one write per frame.
     */
    _scheduleDragFrame() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = 0;
        this._writeDrag();
      });
    }

    /**
     * Cancel any pending drag frame and flush the final position synchronously,
     * so a settle animation starts from the exact release point.
     */
    _flushDragFrame() {
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = 0;
        this._writeDrag();
      }
    }

    /**
     * Write the live drag transform in px using the cached gesture metrics.
     * Read-free — no layout is forced during the drag.
     */
    _writeDrag() {
      if (!this._gestureMetrics) return;
      const { stepPx } = this._gestureMetrics;
      const restPx = -this._index * stepPx;
      this._track.style.transform = `translate3d(${restPx + this._dragDelta}px, 0, 0)`;
      this._updateProgress(-this._dragDelta / stepPx);
    }

    /**
     * Catch an in-flight settle animation on pointerdown: read the track's live
     * interpolated position, freeze it, recentre the DOM, and return the offset
     * carried past the current rest so the caller can reseed the drag from it.
     * Recentring on every catch keeps the conveyor buffer healthy under rapid
     * chained swipes — _index can never drift more than one step from centre.
     * @returns {number} carriedPx — the frozen offset past the rest position.
     */
    _catchMidFlight() {
      const { stepPx } = this._gestureMetrics;
      const matrix = new DOMMatrixReadOnly(
        getComputedStyle(this._track).transform,
      );
      const currentPx = matrix.m41;
      clearTimeout(this._settleTimer);
      this._track.style.transition = "none";

      // Offset carried past rest, computed before recentring (invariant under
      // it, since _recenter is visually invariant at the carried position too).
      const restPx = -this._index * stepPx;
      const carriedPx = currentPx - restPx;
      this._recenter();
      this._syncActiveSlide();
      this._animating = false;

      // Re-apply the same visual position in px. Runs synchronously in this
      // task, so the rest position _recenter set never paints.
      const newRestPx = -this._index * stepPx;
      this._track.style.transform = `translate3d(${newRestPx + carriedPx}px, 0, 0)`;
      return carriedPx;
    }

    /**
     * Animate the track back to the current slide's rest position over the
     * given duration (instant under reduced motion). Used for snap-backs and
     * pointercancel returns; settles via transitionend or the fallback timer.
     * @param {number} durationMs
     */
    _settleBack(durationMs) {
      const reduce = REDUCED_MOTION.matches;
      if (!reduce) {
        this._track.dataset.animating = "true";
        clearTimeout(this._settleTimer);
        this._settleTimer = setTimeout(
          () => this._onTransitionEnd(),
          durationMs + 100,
        );
      }
      this._setTransform(this._index, !reduce, durationMs);
      if (reduce) this._onTransitionEnd();
    }

    /**
     * @param {1|-1} dir
     * @param {boolean} [instant=false]
     * @param {number} [durationMs=DEFAULT_DURATION_MS] Settle duration; gesture
     *   commits pass a short dynamic value, arrows keep the 650ms default.
     */
    _step(dir, instant = false, durationMs = DEFAULT_DURATION_MS) {
      if (this._animating) {
        // Touch/click interrupts everything (D2/T5): fast-forward the in-flight
        // animation to its rest position, then proceed with this step.
        clearTimeout(this._settleTimer);
        this._onTransitionEnd();
      }
      this._animating = true;
      this._index += dir;
      this._navOffset += dir;
      // Reflect at-origin state synchronously so CSS-driven UI (the product-card
      // hover overlay) can react before the arrow animation runs. The overlay's
      // transition is declared only on the matching rule, so flipping this to
      // "false" hides it instantly, leaving the slider's own transition as the
      // only motion the user sees.
      this.dataset.atOrigin = this._navOffset === 0 ? "true" : "false";

      // Lazy-load the slide we're heading into and the one beyond it, so a
      // follow-up step in the same direction is already prefetched. Anything
      // still on data-src is ignored by the browser, so this is the only place
      // secondary card images get a chance to fetch.
      this._loadSlide(this._index);
      this._loadSlide(this._index + dir);

      const reduceMotion = REDUCED_MOTION.matches || instant;

      // Promote the track to its own compositor layer for the animation only;
      // the data attribute is cleared in _onTransitionEnd. A permanent
      // will-change: transform is wasteful for grids with dozens of cards.
      if (!reduceMotion) {
        this._track.dataset.animating = "true";
        // transitionend does not fire if the card is display:none before the
        // transition settles (hidden tab panel, closed drawer). Fall back to a
        // timer slightly longer than the transition so _animating can never
        // wedge shut.
        clearTimeout(this._settleTimer);
        this._settleTimer = setTimeout(
          () => this._onTransitionEnd(),
          durationMs + 100,
        );
      }
      this._setTransform(this._index, !reduceMotion, durationMs);

      // With reduced motion or instant mode we skip the transition, so
      // transitionend never fires; finalise synchronously.
      if (reduceMotion) {
        this._onTransitionEnd();
      }
    }

    /**
     * Swap data-src -> src on every img or video inside the slide at the given
     * DOM index. Indices outside [0, total) are ignored.
     * @param {number} index
     */
    _loadSlide(index) {
      const slide = this._track.children[index];
      if (!slide) return;
      slide.querySelectorAll("img[data-src], video[data-src]").forEach((el) => {
        const src = el.dataset.src;
        if (!src) return;
        if (el.dataset.srcset) {
          el.srcset = el.dataset.srcset;
          delete el.dataset.srcset;
        }
        el.src = src;
        delete el.dataset.src;

        if (el.tagName === "VIDEO") {
          const poster = el.dataset.poster;
          if (poster) {
            el.poster = poster;
            delete el.dataset.poster;
          }
          el.load();
        } else {
          const decode = () => {
            if (typeof el.decode === "function") el.decode().catch(() => {});
          };
          if (el.complete && el.naturalWidth > 0) {
            decode();
          } else {
            el.addEventListener("load", decode, { once: true });
          }
        }
      });
    }

    /**
     * @param {number} index
     * @param {boolean} animate
     * @param {number} [durationMs=DEFAULT_DURATION_MS] Transition duration when
     *   animating. Rest positions still use % maths (once per step, not per
     *   frame); the live drag loop writes px in _writeDrag instead.
     */
    _setTransform(index, animate, durationMs = DEFAULT_DURATION_MS) {
      const gap = parseFloat(getComputedStyle(this._track).columnGap || getComputedStyle(this._track).gap) || 0;
      const trackWidth = this._track.offsetWidth || 1;
      const stepPct = 100 + (gap / trackWidth) * 100;

      this._track.style.transition = animate
        ? `transform ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`
        : "none";
      this._track.style.transform = `translate3d(${-index * stepPct}%, 0, 0)`;
      this._updateProgress(0);
    }

    /**
     * Update the progress-bar custom properties.
     * @param {number} [fraction=0] Live drag fraction within the current step —
     *   negative dragging toward the previous slide, positive toward the next.
     */
    _updateProgress(fraction = 0) {
      if (!this._track || this._track.children.length === 0) return;
      const total = this._track.children.length;
      let currOriginal = parseInt(
        this._track.children[this._index]?.dataset.freshOriginalIndex,
        10,
      );
      if (isNaN(currOriginal)) currOriginal = 0;

      const progress = currOriginal + fraction;

      this.style.setProperty("--media-slider-progress", progress);
      this.style.setProperty("--media-slider-total", total);
    }

    /**
     * After the transform animation completes, normalise `_index`
     * back to the [0, n) range by shifting slides between the ends
     * of the track. Snap the transform back without animation so
     * the next step animates from the new resting position.
     */
    _onTransitionEnd(event) {
      if (
        event &&
        (event.target !== this._track || event.propertyName !== "transform")
      ) {
        return;
      }
      clearTimeout(this._settleTimer);
      this._recenter();
      this._syncActiveSlide();
      this._animating = false;
      // Reset here (not on pointerup) so the synthetic post-drag click is still
      // suppressed while the gesture settles, then genuine clicks work again.
      this._dragDelta = 0;
      delete this._track.dataset.animating;
      delete this._track.dataset.dragging;
    }

    _syncActiveSlide() {
      this._slides.forEach((slide, i) => {
        const isActive = i === this._index;
        if (isActive) {
          slide.removeAttribute("aria-hidden");
          slide.setAttribute("aria-current", "true");
        } else {
          slide.setAttribute("aria-hidden", "true");
          slide.removeAttribute("aria-current");
        }
        // Move every focusable inside an inactive slide out of the
        // tab order. Keep the slide-anchor itself reachable when active.
        slide.querySelectorAll("a, button").forEach((el) => {
          el.setAttribute("tabindex", isActive ? "0" : "-1");
        });
      });
    }
  }

  window.customElements.define("media-slider", MediaSlider);
})();
